// @alamo/ui · DriverPiiBlock · Phase 14 Plan 04 — Tier 2
// Phase 15-07: refit labels (normal-case Poppins) + SegmentedControl para gênero/sinistros
//
// 5 fields: birthDate (Input pill variant), gender (SegmentedControl 4 opts),
// maritalStatus (native pill select), claimsHistory (SegmentedControl 4 opts), bonusClass (Select).
//
// D-PHASE14-1: toggleable prop wraps in accordion "Perfil do motorista" (consultor).
// required prop marks asterisks (público).

import { useState } from 'react';

import { ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '../../lib/cn';
import { Input } from '../Input/Input';
import { SegmentedControl } from '../SegmentedControl/SegmentedControl';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../Select/Select';

export type GenderValue = 'male' | 'female' | 'other' | 'prefer_not_to_say';
export type MaritalStatusValue = 'single' | 'married' | 'divorced' | 'widowed' | 'other';
export type BonusClassValue = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10';

export interface DriverPiiValue {
  birthDate?: string;
  gender?: GenderValue | null;
  maritalStatus?: MaritalStatusValue | null;
  /** Number of claims in last 3 years: 0, 1, 2, 3+ */
  claimsHistory?: '0' | '1' | '2' | '3+' | null;
  bonusClass?: BonusClassValue | null;
}

export interface DriverPiiBlockProps {
  value: DriverPiiValue;
  onChange: (patch: Partial<DriverPiiValue>) => void;
  /** Wraps in "Perfil do motorista" accordion (consultor default hidden). */
  toggleable?: boolean;
  /** Initial expanded state when toggleable. Default false. */
  defaultExpanded?: boolean;
  /** Mark all fields as required (público). */
  required?: boolean;
  /**
   * Compact mode (Plan 15-08): skip birthDate/gender/maritalStatus (assume already
   * rendered by parent — e.g., StepCustomer consultor main form). Only renders
   * driver-specific fields: claimsHistory + bonusClass.
   */
  compact?: boolean;
  /** Override accordion title (default "Perfil do motorista"). */
  accordionTitle?: string;
  errors?: Partial<Record<keyof DriverPiiValue, string>>;
  disabled?: boolean;
  className?: string;
}

// ─── Gender — SegmentedControl 4 items (Phase 15-07 refit) ───────────────────

const GENDER_ITEMS = [
  { key: 'female' as const, label: 'Feminino' },
  { key: 'male' as const, label: 'Masculino' },
  { key: 'other' as const, label: 'Outro' },
  { key: 'prefer_not_to_say' as const, label: 'N/I' },
] as const;

// ─── Claims history — SegmentedControl 4 items (Phase 15-07 refit) ──────────

const CLAIMS_ITEMS = [
  { key: '0' as const, label: 'Nenhum' },
  { key: '1' as const, label: '1 sinistro' },
  { key: '2' as const, label: '2 sinistros' },
  { key: '3+' as const, label: '3 ou mais' },
] as const;

// ─── Marital status ──────────────────────────────────────────────────────────

const MARITAL_STATUS_OPTIONS: { value: MaritalStatusValue; label: string }[] = [
  { value: 'single', label: 'Solteiro(a)' },
  { value: 'married', label: 'Casado(a)' },
  { value: 'divorced', label: 'Divorciado(a)' },
  { value: 'widowed', label: 'Viúvo(a)' },
  { value: 'other', label: 'Outro' },
];

// ─── Bonus class ─────────────────────────────────────────────────────────────

const BONUS_CLASS_OPTIONS: { value: BonusClassValue; label: string }[] = Array.from(
  { length: 11 },
  (_, i) => ({
    value: String(i) as BonusClassValue,
    label: `Classe ${i}${i === 0 ? ' (sem histórico)' : ''}`,
  }),
);

// ─── Pill-style label (Phase 15-07: normal-case Poppins, not uppercase mono) ─

function FieldLabel({
  children,
  required: req,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <span className="text-sm font-medium text-ink/70 font-poppins">
      {children}
      {req ? (
        <span aria-hidden className="ml-0.5 text-erro-apoio">
          *
        </span>
      ) : null}
    </span>
  );
}

// ─── Date mask helper ─────────────────────────────────────────────────────────

function fmtDateMask(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

// ─── PiiFields (shared between accordion and direct render) ──────────────────

function PiiFields({
  value,
  onChange,
  required,
  errors,
  disabled,
  compact,
}: Omit<DriverPiiBlockProps, 'toggleable' | 'defaultExpanded' | 'className' | 'accordionTitle'>) {
  return (
    <div className="flex flex-col gap-4">
      {/* birthDate / gender / maritalStatus — SKIPPED when compact (Plan 15-08).
       *  Parent form (e.g., StepCustomer consultor) already renders these as
       *  customer fields. Driver values copied from customer via useEffect.
       */}
      {!compact && (
        <>
          {/* birthDate — pill Input (Phase 15-07: was DateInput with uppercase label) */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel required={required}>Data de nascimento</FieldLabel>
            <Input
              variant="pill"
              placeholder="Nascimento DD/MM/AAAA"
              inputMode="numeric"
              maxLength={10}
              value={fmtDateMask(value.birthDate ?? '')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange({ birthDate: fmtDateMask(e.target.value) })
              }
              disabled={disabled}
              aria-required={required}
              error={errors?.birthDate}
            />
          </div>

          {/* gender — SegmentedControl 4 options (Phase 15-07: was RadioOptionCards 4 cards) */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel required={required}>Gênero</FieldLabel>
            <SegmentedControl
              items={GENDER_ITEMS}
              value={(value.gender ?? 'prefer_not_to_say') as (typeof GENDER_ITEMS)[number]['key']}
              onChange={(key) => onChange({ gender: key as GenderValue })}
              ariaLabel="Gênero"
              className="w-full justify-between"
            />
            {errors?.gender && (
              <span role="alert" className="text-[11px] font-poppins text-erro-apoio">
                {errors.gender}
              </span>
            )}
          </div>
        </>
      )}

      {/* maritalStatus — native pill-styled select (SKIPPED when compact, Plan 15-08) */}
      {!compact && (
        <div className="flex flex-col gap-1.5">
          <FieldLabel required={required}>Estado civil</FieldLabel>
          <div className="relative">
            <select
              value={value.maritalStatus ?? ''}
              onChange={(e) => onChange({ maritalStatus: e.target.value as MaritalStatusValue })}
              disabled={disabled}
              aria-required={required}
              className={cn(
                'w-full appearance-none rounded-2xl bg-ink/5 px-4 py-3 text-base font-poppins text-ink',
                'min-h-[52px]',
                'outline-none transition-all',
                'focus:bg-white focus:ring-2 focus:ring-laranja-encanto/30',
                !value.maritalStatus && 'text-ink/40',
                errors?.maritalStatus && 'ring-2 ring-erro-apoio/40',
              )}
            >
              <option value="" disabled className="text-ink/40">
                Estado civil
              </option>
              {MARITAL_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} className="text-ink">
                  {opt.label}
                </option>
              ))}
            </select>
            {/* chevron indicator */}
            <span
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-ink/40"
              aria-hidden="true"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 6l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
          {errors?.maritalStatus && (
            <span role="alert" className="text-[11px] font-poppins text-erro-apoio">
              {errors.maritalStatus}
            </span>
          )}
        </div>
      )}

      {/* claimsHistory — SegmentedControl 4 options (Phase 15-07: was RadioOptionCards 4 cards) */}
      <div className="flex flex-col gap-1.5">
        <FieldLabel required={required}>Sinistros nos últimos 3 anos</FieldLabel>
        <SegmentedControl
          items={CLAIMS_ITEMS}
          value={(value.claimsHistory ?? '0') as (typeof CLAIMS_ITEMS)[number]['key']}
          onChange={(key) => onChange({ claimsHistory: key as DriverPiiValue['claimsHistory'] })}
          ariaLabel="Sinistros nos últimos 3 anos"
          className="w-full justify-between"
        />
        {errors?.claimsHistory && (
          <span role="alert" className="text-[11px] font-poppins text-erro-apoio">
            {errors.claimsHistory}
          </span>
        )}
      </div>

      {/* bonusClass — Radix Select */}
      <div className="flex flex-col gap-1.5">
        <FieldLabel required={required}>Classe de bônus</FieldLabel>
        <Select
          value={value.bonusClass ?? ''}
          onValueChange={(v) => onChange({ bonusClass: v as BonusClassValue })}
          disabled={disabled}
        >
          <SelectTrigger
            aria-required={required}
            className={cn(errors?.bonusClass && 'border-erro-apoio')}
          >
            {value.bonusClass !== undefined && value.bonusClass !== null
              ? BONUS_CLASS_OPTIONS.find((o) => o.value === value.bonusClass)?.label
              : 'Selecione a classe...'}
          </SelectTrigger>
          <SelectContent>
            {BONUS_CLASS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors?.bonusClass && (
          <span role="alert" className="text-[11px] font-poppins text-erro-apoio">
            {errors.bonusClass}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * DriverPiiBlock — 5 PII fields for driver profile.
 *
 * Phase 15-07: labels refit to normal-case Poppins (not uppercase mono).
 * Phase 15-07: gender + claimsHistory refit to SegmentedControl (not RadioOptionCards).
 *
 * toggleable=true wraps in accordion "Perfil do motorista" (consultor default hidden).
 * required=true marks asterisks (público use).
 * D-PHASE14-1: driver PII visible as toggle in consultor; required in público.
 */
export function DriverPiiBlock({
  value,
  onChange,
  toggleable = false,
  defaultExpanded = false,
  required = false,
  compact = false,
  accordionTitle = 'Perfil do motorista',
  errors,
  disabled,
  className,
}: DriverPiiBlockProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!toggleable) {
    return (
      <div className={className}>
        <PiiFields
          value={value}
          onChange={onChange}
          required={required}
          errors={errors}
          disabled={disabled}
          compact={compact}
        />
      </div>
    );
  }

  return (
    <div className={cn('rounded-xl border border-slate-200 bg-white overflow-hidden', className)}>
      {/* Accordion header */}
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        aria-expanded={expanded}
        aria-controls="driver-pii-body"
        className={cn(
          'flex w-full items-center justify-between px-4 py-3.5',
          'min-h-[44px] font-poppins text-sm font-semibold text-ink',
          'hover:bg-slate-50 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-azul-protecao',
        )}
      >
        <span>{accordionTitle}</span>
        <span aria-hidden>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          )}
        </span>
      </button>

      {/* Accordion body */}
      {expanded && (
        <div id="driver-pii-body" className="px-4 pb-4">
          <PiiFields
            value={value}
            onChange={onChange}
            required={required}
            errors={errors}
            disabled={disabled}
            compact={compact}
          />
        </div>
      )}
    </div>
  );
}
